/**
 * Hook to determine the active top-level category for navbar highlighting
 * based on current navigation context.
 *
 * Returns "Menswear", "Womenswear", "Kidswear", "Beauty", or null
 */
"use client";

import { useEffect, useState } from "react";

import { usePathname } from "next/navigation";

import { useSearchContext } from "@/contexts/SearchContext";
import { getResultsThreadIdFromPath } from "@/lib/route-paths";
import { mapGenderToCategory } from "@/utils/genderMapping";

export function useActiveCategory(): string | null {
  const pathname = usePathname();
  const { activeSearchParams } = useSearchContext();
  const [activeCategory, setActiveCategory] = useState<string | null>(null);

  useEffect(() => {
    // Not on results page - no category should be highlighted
    const threadId = getResultsThreadIdFromPath(pathname);
    if (!threadId) {
      setActiveCategory(null);
      return;
    }

    if (typeof window === "undefined") {
      setActiveCategory(null);
      return;
    }

    const computeActiveCategory = (): string | null => {
      const navbarCategory = sessionStorage.getItem(`navbar-category-${threadId}`);
      // If navbar-category key exists in sessionStorage, use its value directly.
      // - Non-empty string: return that category for highlighting
      // - Empty string: explicitly "no category" (brand-only or M+W navigation), returns null
      if (navbarCategory !== null) {
        return navbarCategory || null;
      }

      const browseMenuData = sessionStorage.getItem(`browse-menu-${threadId}`);
      if (browseMenuData) {
        try {
          const { display_path: displayPath } = JSON.parse(browseMenuData);
          if (Array.isArray(displayPath) && displayPath.length > 0) {
            return displayPath[0];
          }
        } catch (error) {
          console.error("Failed to parse browse menu data:", error);
        }
      }

      const chatData = sessionStorage.getItem(`chat-${threadId}`);
      if (chatData) {
        try {
          const { gender: displayGender } = JSON.parse(chatData);
          const category = mapGenderToCategory(displayGender);
          if (category !== null) return category;
        } catch (error) {
          console.error("Failed to parse chat data:", error);
        }
      }

      const searchData = sessionStorage.getItem(`search-${threadId}`);
      if (searchData) {
        try {
          const { gender: displayGender } = JSON.parse(searchData);
          const category = mapGenderToCategory(displayGender);
          if (category !== null) return category;
        } catch (error) {
          console.error("Failed to parse search data:", error);
        }
      }

      const gender = activeSearchParams?.gender;
      if (gender) {
        return mapGenderToCategory(gender);
      }

      return null;
    };

    setActiveCategory(computeActiveCategory());
  }, [pathname, activeSearchParams]);

  return activeCategory;
}
